import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
  setHasFetchedData,
} from "../../../../redux/rewardType/rewardType";
import {
  getListRewardType,
  updateRewardType,
} from "../../../../api/rewardType/rewardType";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import RowActions from "../../../../components/cms/RowActions";
import { ACTION_ICON } from "../../../../components/cms/actions";
import { neutral } from "../../../../theme/tokens";
import BrandedPageLayout from "../../../../components/Dashboard/BrandedPageLayout";
import ListToolbar from "../../../../components/cms/ListToolbar";
import CmsTableCard from "../../../../components/cms/CmsTableCard";
import { Button, Empty, Form, Input, message } from "antd";

export default function RewardTypeTable() {
  const {
    listRewardTypes,
    isLoadRewardTypes,
    currentPage,
    pageSize,
    hasFetchedData,
  } = useSelector((state) => state.persistedReducer.rewardTypeSlice);

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});

  // Gọi API lấy danh sách khi component mount hoặc khi currentPage/pageSize thay đổi
  useEffect(() => {
    dispatch(setHasFetchedData(true));

    dispatch(getListRewardType({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  // Kiểm tra xem dòng có đang được chỉnh sửa không
  const isEditing = (record) => record.id === editingKey;

  // Bắt đầu chỉnh sửa
  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
    setEditedRecord(record);
  };

  // Hủy chỉnh sửa
  const cancel = () => {
    setEditingKey("");
  };

  // Lưu dữ liệu đã chỉnh sửa
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const updatedData = { ...row };
      const response = await dispatch(
        updateRewardType({ id, data: updatedData })
      );
      if (updateRewardType.fulfilled.match(response)) {
        message.success("Sửa thành công");
        await dispatch(getListRewardType({ currentPage, pageSize }));
      } else if (updateRewardType.rejected.match(response)) {
        if (response.payload.message) {
          message.error(response.payload.message);
        }
        if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        }
      }
      setEditingKey("");
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "Mã loại cơ chế",
      dataIndex: "code",
      key: "code",
      fixed: 'left',
      width: 200,
    },
    {
      title: "Tên loại cơ chế",
      dataIndex: "name",
      key: "name",
      width: 220,
      ellipsis: true,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: "Tên loại cơ chế không được để trống",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.name
        );
      },
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item name="description" style={{ margin: 0 }}>
            <Input />
          </Form.Item>
        ) : (
          record.description
        );
      },
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record); // Kiểm tra nếu dòng đang chỉnh sửa
        return editable ? (
          <>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)} // Lưu dữ liệu
              style={{ marginRight: 8 }}
            >
              Lưu
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel} // Hủy chỉnh sửa
              danger
              style={{ color: neutral[500] }}
            >
              Hủy
            </Button>
          </>
        ) : (
          <RowActions
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => edit(record),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <BrandedPageLayout
      title="Loại cơ chế tích điểm"
      subtitle="Danh mục loại cơ chế dùng khi thiết lập cơ chế tích điểm cho từng dịch vụ."
      breadcrumbItems={[{ title: "Dịch vụ" }, { title: "Loại cơ chế" }]}
    >
      {/* Màn này không có bộ lọc — toolbar chỉ giữ nút Làm mới cho nhất quán. */}
      <ListToolbar
        loading={isLoadRewardTypes}
        onReload={() => dispatch(getListRewardType({ currentPage, pageSize }))}
      />

      <Form form={form} component={false}>
        <CmsTableCard
          columns={columns}
          loading={listRewardTypes?.length === 0 ? isLoadRewardTypes : false}
          dataSource={isLoadRewardTypes ? [] : listRewardTypes}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: listRewardTypes?.length,
            onChange: (page, size) => {
              dispatch(setCurrentPage(page));
              dispatch(setPageSize(size));
            },
          }}
          locale={{
            emptyText: <Empty description="Không có loại cơ chế tích điểm!" />,
          }}
          bordered
          scroll={{ x: "max-content" }}
        />
      </Form>
    </BrandedPageLayout>
  );
}
